import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #revealedCards = new WeakSet();
  #pendingCards = new Set();
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #cardObserver = new IntersectionObserver(
    (entries) => {
      const visibleCards = entries
        .filter((entry) => entry.isIntersecting)
        .map((entry) => entry.target)
        .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

      visibleCards.forEach((card, index) => {
        this.#revealCard(card, Math.min(index * 90, 450));
      });
    },
    { threshold: 0, rootMargin: '0px 0px -24px 0px' }
  );
  #gridObserver = new MutationObserver(() => this.#observeCards());

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#reducedMotion.addEventListener('change', this.#handleMotionChange);
    this.addEventListener('focusin', this.#handleCardFocus);
    this.#observeCards(true);
    this.#gridObserver.observe(this, { childList: true, subtree: true });
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#reducedMotion.removeEventListener('change', this.#handleMotionChange);
    this.removeEventListener('focusin', this.#handleCardFocus);
    this.#gridObserver.disconnect();
    this.#cardObserver.disconnect();
    for (const card of this.#pendingCards) this.#revealCard(card);
  }

  #observeCards(initial = false) {
    for (const card of this.#pendingCards) {
      if (!this.contains(card)) {
        this.#cardObserver.unobserve(card);
        this.#pendingCards.delete(card);
      }
    }

    for (const card of this.querySelectorAll('.product-grid > .product-grid__item')) {
      if (this.#revealedCards.has(card) || this.#pendingCards.has(card)) continue;

      if (this.#reducedMotion.matches || (initial && card.getBoundingClientRect().top < window.innerHeight)) {
        this.#revealCard(card);
        continue;
      }

      card.classList.add('product-grid__item--reveal-pending');
      this.#pendingCards.add(card);
      this.#cardObserver.observe(card);
    }
  }

  #revealCard(card, delay = 0) {
    if (!(card instanceof HTMLElement)) return;

    card.style.setProperty('--card-reveal-delay', `${delay}ms`);
    card.classList.remove('product-grid__item--reveal-pending');
    this.#pendingCards.delete(card);
    this.#revealedCards.add(card);
    this.#cardObserver.unobserve(card);
  }

  #handleMotionChange = () => {
    if (this.#reducedMotion.matches) {
      for (const card of this.#pendingCards) this.#revealCard(card);
    }
  };

  #handleCardFocus = ({ target }) => {
    if (!(target instanceof Element)) return;
    const card = target.closest('.product-grid__item');
    if (card) this.#revealCard(card);
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
